import { MantineColor } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { StatusBadgeHelp } from "@dehub/theme";
import { ReturnRequestStatus } from "../../../apis/return";
import { DotPill, Tone } from "../../delivery/atoms";
import { StatusPill, toneOfColor } from "../../ui/status-pill";

/**
 * Status tone (DeHUB UI standard, canvas Ord_Rules «в»): green = done, amber = waiting on someone,
 * grey = closed without outcome, red = needs attention. Colour lives only in the dot and the pale
 * background; the text stays dark slate.
 */
export const RETURN_STATUS_TONE: Record<ReturnRequestStatus, Tone> = {
  REQUESTED                 : "amber",
  PROPOSED                  : "amber",
  ACCEPTED                  : "green",
  AWAITING_PICKUP_SCHEDULE  : "amber",
  AWAITING_SHIPMENT_INFO    : "amber",
  IN_TRANSIT                : "amber",
  IN_TRANSIT_EXCEPTION      : "red",
  AWAITING_WAREHOUSE_RECEIPT: "amber",
  AWAITING_RESOLUTION       : "amber",
  REFUND_PENDING            : "amber",
  REJECTED                  : "grey",
  DISPUTED                  : "red",
  COMPLETED                 : "green",
  PARTIALLY_COMPLETED       : "green",
  CANCELLED                 : "grey"
};

interface Props {
  status: ReturnRequestStatus;
  /** Kept for call-site compatibility; the dot pill has one size. */
  size?: "xs" | "sm" | "md" | "lg";
  /** Optional color override map (buyer surface). Its colours only pick the
   *  tone of the same status pill. */
  colorMap?: Partial<Record<ReturnRequestStatus, MantineColor>>;
  /** Supplier screens: hover popover from `order.return-status` (@dehub/help R5). */
  help?: boolean;
}

export function ReturnStatusBadge ({ status, colorMap, help }: Props){
  const { t } = useTranslation("returns");
  const label = t(`status.${status}`, { defaultValue: "-" });
  if (colorMap) {
    return <StatusPill tone={toneOfColor(colorMap[status] ?? "gray")}>{label}</StatusPill>;
  }
  const pill = <DotPill tone={RETURN_STATUS_TONE[status] ?? "grey"}>{label}</DotPill>;
  return help ? <StatusBadgeHelp helpNs="order.return-status" code={status} name={label}>{pill}</StatusBadgeHelp> : pill;
}
